import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, History, Loader2 } from 'lucide-react';

import { useApi } from '../../lib/api';
import type { ApiResponse, ProposalRun } from '../../types';
import { EtapesDuRun, StatusBadge } from './parts';

/**
 * Journal des exécutions passées, réflexion du modèle comprise.
 *
 * Les étapes et les pas du modèle étaient déjà conservés : chaque exécution
 * garde ses événements en base, et rien ne les purge. Ce qui manquait était le
 * moyen de les revoir. Le suivi ne s'affichait que pendant une génération, donc
 * tout ce que le modèle avait cherché, consulté et calculé devenait invisible à
 * la seconde où le document était produit, alors que c'est précisément après
 * coup qu'on veut comprendre d'où sort un chiffre.
 *
 * Les exécutions sont repliées par défaut : leur journal fait plusieurs dizaines
 * de lignes, et une page qui les déroulerait toutes noierait le reste. Le
 * chargement suit le dépli, ce qui évite de rapatrier des milliers d'événements
 * pour une proposition qu'on ne fait que survoler.
 */
export function JournalDesRuns({ proposalId, runs }: {
    proposalId: string;
    runs: ProposalRun[];
}) {
    const [ouvert, setOuvert] = useState<string | null>(null);
    const [deplie, setDeplie] = useState(false);

    // Les exécutions en cours ont déjà leur suivi en haut de page : ce journal
    // est celui de ce qui est terminé.
    const passees = runs.filter(
        (run) => run.status !== 'queued' && run.status !== 'running',
    );
    if (passees.length === 0) return null;

    return (
        <div className="card space-y-3">
            <button
                className="flex w-full items-center gap-2 text-left"
                onClick={() => setDeplie((etat) => !etat)}
            >
                {deplie
                    ? <ChevronDown className="h-4 w-4 text-[var(--color-text-secondary)]" />
                    : <ChevronRight className="h-4 w-4 text-[var(--color-text-secondary)]" />}
                <History className="h-4 w-4 text-[var(--color-text-secondary)]" />
                <span className="text-sm font-medium text-[var(--color-text)]">
                    Journal des générations
                </span>
                <span className="text-xs text-[var(--color-text-muted)]">
                    {passees.length} exécution{passees.length > 1 ? 's' : ''}
                </span>
            </button>

            {deplie && (
                <div className="space-y-2">
                    {passees.map((run) => (
                        <div
                            key={run.id}
                            className="rounded-lg border border-[var(--color-border)]"
                        >
                            <button
                                className="flex w-full items-center justify-between gap-3 p-2.5 text-left
                                    hover:bg-[var(--color-bg-hover)]"
                                onClick={() => setOuvert(ouvert === run.id ? null : run.id)}
                            >
                                <span className="flex min-w-0 items-center gap-2">
                                    {ouvert === run.id
                                        ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-muted)]" />
                                        : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-muted)]" />}
                                    <span className="text-xs text-[var(--color-text-secondary)]">
                                        {quand(run.startedAt || run.createdAt)}
                                    </span>
                                    <span className="truncate text-xs text-[var(--color-text-muted)]">
                                        {run.model}
                                    </span>
                                </span>
                                <span className="flex shrink-0 items-center gap-2">
                                    {/* Le coût et les jetons sont dits ici : c'est le
                                        seul endroit où l'on compare deux exécutions. */}
                                    <span className="text-xs text-[var(--color-text-muted)]">
                                        {(run.outputTokens || 0).toLocaleString('fr-FR')} jetons
                                    </span>
                                    {run.costUsd > 0 && (
                                        <span className="text-xs text-[var(--color-text-muted)]">
                                            {run.costUsd.toFixed(2)} $
                                        </span>
                                    )}
                                    <StatusBadge status={run.status} />
                                </span>
                            </button>

                            {ouvert === run.id && (
                                <EtapesDuRunChargees
                                    proposalId={proposalId}
                                    runId={run.id}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function quand(iso: string): string {
    const date = new Date(iso);
    return date.toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
}

/** Étapes d'une exécution terminée, chargées au dépli. */
export function EtapesDuRunChargees({ proposalId, runId }: {
    proposalId: string;
    runId: string;
}) {
    const api = useApi();
    const { data, isLoading } = useQuery({
        queryKey: ['proposal-run', runId],
        queryFn: () => api.get<ApiResponse<ProposalRun>>(
            `/api/proposals/${proposalId}/runs/${runId}`,
        ),
        // Une exécution terminée ne bouge plus : rien à rafraîchir.
        staleTime: Infinity,
    });

    if (isLoading) {
        return (
            <div className="flex justify-center p-4">
                <Loader2 className="h-4 w-4 animate-spin text-[var(--color-primary)]" />
            </div>
        );
    }

    const evenements = data?.data.events || [];
    if (evenements.length === 0) {
        return (
            <p className="border-t border-[var(--color-border)] p-3 text-xs text-[var(--color-text-muted)]">
                Aucune étape enregistrée pour cette exécution.
            </p>
        );
    }

    return (
        <div className="border-t border-[var(--color-border)] p-3">
            {data?.data.errorMessage && (
                <p className="mb-2 text-xs text-[var(--color-error)]">
                    {data.data.errorMessage}
                </p>
            )}
            <EtapesDuRun evenements={evenements} />
        </div>
    );
}
